/* Het schuifbeeld op het formulier van een energieoplossing (ADR 0024): twee beelden van dezelfde plek
 * over elkaar, met een schuif van voor naar na. Dit bestand tekent alleen. De vorm, wat de site ervan
 * toont en wat er wordt geschreven, staat in data/schuifbeeld.ts.
 *
 * De kolom hoort bij een script dat de eigenaar van de database draait: heeft de rij hem niet, dan
 * staat dat er, zoals bij "In het kort" (Aanvullingen.tsx). Een schuifbeeld dat niemand aanraakt, wordt
 * bij opslaan niet geschreven. */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Gewijzigd, NaOpslaan, NietBeschikbaar } from './Aanvullingen';
import {
  KANTEN, KOLOM, LABEL, SCRIPT, STAAT_ZIN, heeftSchuifbeeld, isLeeg, leesSchuifbeeld, maakLeeg, staatVan, teSchrijven, zetBeeld, zetTekst,
} from './data/schuifbeeld';
import type { Rij } from './data/website';
import { Beeldveld } from './MediaKiezer';

/** `vorige` is de rij in de database (bij iets nieuws niets), `waarde` het object in het concept. */
export function SchuifbeeldKaart({ vorige, waarde, fout, uit, zet }: { vorige: Rij | null; waarde: unknown; fout?: string; uit: boolean; zet: (waarde: unknown) => void }) {
  if (!vorige) return <NaOpslaan kaart={KOLOM} titel={LABEL} />;
  if (!heeftSchuifbeeld(vorige)) return <NietBeschikbaar kaart={KOLOM} titel={LABEL} script={SCRIPT} />;

  const opgeslagen = vorige[KOLOM];
  const basis = leesSchuifbeeld(opgeslagen);
  const lezing = basis.ok ? leesSchuifbeeld(waarde) : basis;
  if (!lezing.ok) {
    return (
      <Card data-kaart={KOLOM} data-kaart-staat="alleen-lezen">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">{LABEL} <Badge variant="secondary">Alleen lezen</Badge></CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Dit schuifbeeld heeft een vorm die de bewerker niet kent. {lezing.reden} Daarom is het hier niet te wijzigen. Het blijft bij opslaan ongewijzigd.
          </p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border p-3 text-xs text-muted-foreground">{JSON.stringify(opgeslagen ?? null, null, 2)}</pre>
        </CardContent>
      </Card>
    );
  }

  const gewijzigd = teSchrijven(opgeslagen, waarde) !== null || !!fout;
  const staat = staatVan(waarde);
  const zelfde = lezing.voor.beeld.trim() !== '' && lezing.voor.beeld.trim() === lezing.na.beeld.trim();
  return (
    <Card data-kaart={KOLOM} data-kaart-staat={gewijzigd ? 'gewijzigd' : 'ongewijzigd'}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{LABEL}</CardTitle>
          {gewijzigd && <Gewijzigd uit={uit} terug={() => zet(opgeslagen)} />}
        </div>
        <p className="text-sm text-muted-foreground">
          Twee beelden van dezelfde plek over elkaar, met een schuif waarmee de bezoeker van voor naar na gaat. Bijvoorbeeld van luchtfoto naar laadplein.
          Op de site staat het onder de tekst van deze oplossing. Zonder twee beelden toont de site niets, ook de kop en de tekst niet.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="grid gap-3 lg:grid-cols-2">
          {KANTEN.map(({ kant, naam, standaard, plek }) => {
            const beeld = kant === 'before' ? lezing.voor : lezing.na;
            const id = `${KOLOM}-${kant}`;
            return (
              <li key={kant} className="space-y-3 rounded-md border p-3" data-beeld={kant}>
                <p className="text-sm font-medium">{naam} <span className="font-normal text-muted-foreground">({plek})</span></p>
                <div className="space-y-1.5">
                  <Label htmlFor={`${id}-image`}>Beeld</Label>
                  <Beeldveld id={`${id}-image`} waarde={beeld.beeld} uit={uit} zet={(pad) => zet(zetBeeld(waarde, kant, 'image', pad))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${id}-caption`}>Bijschrift (mag leeg blijven)</Label>
                  <Input id={`${id}-caption`} value={beeld.bijschrift} placeholder={standaard} disabled={uit} onChange={(e) => zet(zetBeeld(waarde, kant, 'caption', e.target.value))} />
                  <p className="text-xs text-muted-foreground">Twee of drie woorden, in een hoek van het beeld. Leeg betekent: er staat {standaard}.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${id}-alt`}>Beschrijving (mag leeg blijven)</Label>
                  <Input id={`${id}-alt`} value={beeld.beschrijving} disabled={uit} onChange={(e) => zet(zetBeeld(waarde, kant, 'alt', e.target.value))} />
                  {/* De volgorde van de site (Schuifbeeld.tsx in de site-repo): de eigen beschrijving, anders die van het bestand in de media, anders het bijschrift. */}
                  <p className="text-xs text-muted-foreground">Wat er op het beeld te zien is, voor wie het niet ziet. Leeg betekent: de beschrijving die bij dit beeld in de media staat. Staat daar geen, dan geldt het bijschrift.</p>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="grid gap-3 lg:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor={`${KOLOM}-title`}>Kop naast het beeld (mag leeg blijven)</Label>
            <Input id={`${KOLOM}-title`} value={lezing.kop} disabled={uit} onChange={(e) => zet(zetTekst(waarde, 'title', e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${KOLOM}-credit`}>Bronvermelding (mag leeg blijven)</Label>
            <Input id={`${KOLOM}-credit`} value={lezing.bron} disabled={uit} onChange={(e) => zet(zetTekst(waarde, 'credit', e.target.value))} />
            <p className="text-xs text-muted-foreground">Klein onder het beeld. Nodig als de maker van de luchtfoto dat vraagt.</p>
          </div>
          <div className="space-y-1.5 lg:col-span-2">
            <Label htmlFor={`${KOLOM}-text`}>Tekst onder de kop (mag leeg blijven)</Label>
            <Textarea id={`${KOLOM}-text`} rows={2} value={lezing.tekst} disabled={uit} onChange={(e) => zet(zetTekst(waarde, 'text', e.target.value))} />
            <p className="text-xs text-muted-foreground">Eén of twee zinnen. Gewone tekst, geen HTML.</p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          De twee beelden liggen precies over elkaar. Geef ze dezelfde uitsnede en dezelfde maat: liggend, 16 bij 9, liefst 2400 bij 1350 pixels. Zet geen tekst
          in het beeld zelf. Een luchtfoto laat zien waar het is: gebruik alleen een terrein waarvan de eigenaar dat goed vindt, en ga na of de foto op de site mag.
        </p>
        {zelfde && <p className="text-xs text-amber-600" data-schuifbeeld-melding="zelfde">Voor en na zijn nu hetzelfde beeld: de schuif laat dan geen verschil zien.</p>}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm" data-schuifbeeld={staat}>{STAAT_ZIN[staat]}</p>
          {!isLeeg(waarde) && <Button type="button" variant="outline" size="sm" disabled={uit} onClick={() => zet(maakLeeg(waarde))}>Maak het schuifbeeld leeg</Button>}
        </div>
        {fout && <p className="text-xs text-destructive" role="alert">{fout}</p>}
      </CardContent>
    </Card>
  );
}
